import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BadgeCheck, Check, Paperclip, Wallet, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useDecideExpenseMutation, useExpensesQuery, useOpenDocument, useReimburseExpenseMutation } from '@/client/api/business/spend';
import { EXPENSE_STATUS_BADGE, EXPENSE_STATUS_LABEL, type Expense, type ExpenseStatus } from '@/client/types/business/spend';
import { AccountingPageShell } from './components/AccountingPageShell';
import { Field, inputClass, useSpendLookups } from './components/spend-ui';
import { EmployeeAvatar, EmployeeLink, EmptyState, RowMenu, StatusBadge } from '../HumanResource/components/hr-ui';
import { DecisionDialog } from '../HumanResource/components/DecisionDialog';
import { money, totals } from '../HumanResource/components/comp-format';
import { formatDay } from '../HumanResource/components/leave-format';
import { useEmployees } from '../HumanResource/hr-store';

const TABS: (ExpenseStatus | 'all')[] = ['submitted', 'approved', 'reimbursed', 'rejected', 'all'];
const TAB_LABEL: Record<string, string> = { submitted: 'To decide', approved: 'To reimburse', reimbursed: 'Reimbursed', rejected: 'Rejected', all: 'All' };

/** Employee claims. People file them in My workplace; managers usually decide
 *  them there. Accounting can decide any, and pays them back. */
export default function ExpensesPage() {
  const expensesQ = useExpensesQuery();
  const employees = useEmployees();
  const lookups = useSpendLookups();
  const decide = useDecideExpenseMutation();
  const reimburse = useReimburseExpenseMutation();
  const openDoc = useOpenDocument('business');
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const [tab, setTab] = useState<(typeof TABS)[number]>('submitted');
  const [deciding, setDeciding] = useState<{ expense: Expense; approve: boolean } | null>(null);
  const [paying, setPaying] = useState<Expense | null>(null);
  const [ref, setRef] = useState('');

  const expenses = expensesQ.data ?? [];
  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const shown = expenses.filter((x) => tab === 'all' || x.status === tab);
  const stats = [
    { key: 'decide', label: 'Waiting for a decision', value: totals(expenses.filter((x) => x.status === 'submitted')) },
    { key: 'owe', label: 'Approved, to pay back', value: totals(expenses.filter((x) => x.status === 'approved')) },
    { key: 'noreceipt', label: 'Open claims without a receipt', value: expenses.filter((x) => (x.status === 'submitted' || x.status === 'approved') && !x.hasReceipt).length },
    {
      key: 'paid',
      label: 'Paid back this month',
      value: totals(expenses.filter((x) => x.status === 'reimbursed' && (x.reimbursedAt ?? '').startsWith(new Date().toISOString().slice(0, 7)))),
    },
  ];
  const done = (msg: string) => ({ onSuccess: () => toast.success(msg), onError: (e: unknown) => toast.error((e as Error).message) });

  return (
    <AccountingPageShell
      mock={false}
      title="Expenses"
      description="What employees paid out of pocket. They file claims in My workplace and their manager approves; accounting pays them back here."
      crumbs={[{ label: 'Accounting', href: '/admin/accounting' }, { label: 'Expenses' }]}
      icon={Wallet}
      stats={stats}
    >
      <div className="mb-4 inline-flex flex-wrap rounded-lg border border-gray-200 bg-white p-1">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === t ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {expensesQ.isError ? (
        <EmptyState label={`Could not load expenses: ${(expensesQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : expensesQ.isLoading ? (
        <EmptyState label="Loading expenses…" />
      ) : shown.length === 0 ? (
        <EmptyState label="Nothing here." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 font-medium">What</th>
                <th className="px-4 py-3 font-medium">Spent</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((x) => {
                const e = byId.get(x.employeeId);
                return (
                  <tr key={x.id}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <EmployeeAvatar employee={e} size="sm" />
                        <EmployeeLink employee={e} />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-800">{x.description}</p>
                      <p className="text-xs text-gray-400">
                        {lookups.categoryName(x.categoryId)}
                        {x.hasReceipt ? ' · receipt attached' : ' · no receipt'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDay(x.spentOn)}</td>
                    <td className="px-4 py-3 text-gray-900">{money(x.amount, x.currency)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge label={EXPENSE_STATUS_LABEL[x.status]} className={EXPENSE_STATUS_BADGE[x.status]} />
                      {x.decisionNote && <p className="text-[11px] text-gray-400">“{x.decisionNote}”</p>}
                      {x.reimbursedRef && <p className="text-[11px] text-gray-400">Ref {x.reimbursedRef}</p>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowMenu
                        label="Expense actions"
                        items={[
                          { label: 'Open receipt', icon: Paperclip, hidden: !x.hasReceipt, onSelect: () => openDoc(`/expenses/${x.id}/receipt`).catch((err) => toast.error((err as Error).message)) },
                          { label: 'Approve', icon: Check, hidden: !canManage || x.status !== 'submitted', onSelect: () => setDeciding({ expense: x, approve: true }) },
                          { label: 'Reject', icon: XIcon, danger: true, hidden: !canManage || x.status !== 'submitted', onSelect: () => setDeciding({ expense: x, approve: false }) },
                          {
                            label: 'Mark reimbursed',
                            icon: BadgeCheck,
                            hidden: !canManage || x.status !== 'approved',
                            onSelect: () => {
                              setRef('');
                              setPaying(x);
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DecisionDialog
        open={!!deciding}
        approve={deciding?.approve ?? true}
        title={deciding?.approve ? 'Approve this claim?' : 'Reject this claim?'}
        description={
          deciding
            ? `${byId.get(deciding.expense.employeeId)?.name ?? 'Employee'}: ${deciding.expense.description}, ${money(deciding.expense.amount, deciding.expense.currency)}.${
                deciding.expense.hasReceipt ? '' : ' There is no receipt.'
              }`
            : ''
        }
        busy={decide.isPending}
        onCancel={() => setDeciding(null)}
        onConfirm={(note) => {
          if (deciding) decide.mutate({ id: deciding.expense.id, decision: deciding.approve ? 'approve' : 'reject', note }, done(deciding.approve ? 'Approved' : 'Rejected'));
          setDeciding(null);
        }}
      />

      <Dialog open={!!paying} onOpenChange={(o) => !o && setPaying(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark reimbursed</DialogTitle>
          </DialogHeader>
          {paying && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                reimburse.mutate({ id: paying.id, reference: ref }, done('Marked reimbursed'));
                setPaying(null);
              }}
            >
              <p className="text-sm text-gray-600">
                Pay {byId.get(paying.employeeId)?.name ?? 'the employee'} {money(paying.amount, paying.currency)}, then record it here.
              </p>
              <Field label="Payment reference (bank / MoMo)">
                <input value={ref} onChange={(e) => setRef(e.target.value)} className={inputClass} />
              </Field>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setPaying(null)}>
                  Cancel
                </Button>
                <Button type="submit">Mark reimbursed</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AccountingPageShell>
  );
}
